import { ABaseModel } from '../abstracts/ABaseModel.js';
import {
  IUser,
  IPrivacySetting,
  IAccount,
  IAuthor,
  IAuthorLocale,
  ICuratorThought,
  ICuratorThoughtPaginationResult,
  ICustomerCredit,
  IUserAddress,
  IUserAddressListResult,
  IUserCard,
  IUserCardListResult,
  IFollow,
  IFollowListResult,
  IFollowPaginationResult,
} from '../interfaces/User.js';
import { Image } from './Image.js';
import { PaginationResult, ListResult } from './List.js';

/**
 * User
 */
class User extends ABaseModel implements IUser {
  id: number;
  visitorId: number;
  status: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  lastLogin: string;
  birthDate: string;
  city: string;
  countryCode: string;
  gender: string;
  phone: string;
  registrationDate: string;
  timeZone: string;
  website: string;
  quizDate: string;
  quizPersonality: string;
  marketingCampaignId: number;
  marketingCampaignCode: string;
  marketingCampaignType: string;
  accounts: [Account];
  avatars: [Image];
  privacySettings: [PrivacySetting];

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * PrivacySetting
 */
class PrivacySetting extends ABaseModel implements IPrivacySetting {
  id: number;
  userId: number;
  category: string;
  name: string;
  type: number;
  value: any;
  order: number;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * Account
 */
class Account extends ABaseModel implements IAccount {
  id: number;
  status: number;
  domain: string;
  entityId: string;
  email: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * Author
 */
class Author extends ABaseModel implements IAuthor {
  id: number;
  type: string;
  typeId: number;
  typeRole: string;
  typeAlias: string;
  fullName: string;
  biography: string;
  avatars: Array<Image>;
  locales: Array<AuthorLocale>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * AuthorLocale
 */
class AuthorLocale extends ABaseModel implements IAuthorLocale {
  id: number;
  localeCode: string;
  biography: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CuratorThought
 */
class CuratorThought extends ABaseModel implements ICuratorThought {
  id: string;
  editorialId: number;
  parentType: string;
  parentId: number;
  text: string;
  author: Author;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CuratorThoughtPaginationResult
 */
class CuratorThoughtPaginationResult extends PaginationResult implements ICuratorThoughtPaginationResult {
  declare items: Array<CuratorThought>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CustomerCredit
 */
class CustomerCredit extends ABaseModel implements ICustomerCredit {
  customerId: string;
  value: number;
  currency: string;
  storeCode: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * UserAddress
 */
class UserAddress extends ABaseModel implements IUserAddress {
  id: number;
  type: string;
  lastUsed: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  countyCode: string;
  postcode: string;
  countryCode: string;
  notes: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * UserAddressListResult
 */
class UserAddressListResult extends ListResult implements IUserAddressListResult {
  declare items: Array<UserAddress>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * UserCard
 */
class UserCard extends ABaseModel implements IUserCard {
  id: string;
  lastUsed: string;
  canUse: boolean;
  method: string;
  cardId: string;
  cardName: string;
  cardType: string;
  cardLast4: string;
  cardEndMonth: number;
  cardEndYear: number;
  billingAddress: UserAddress;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * UserCardListResult
 */
class UserCardListResult extends ListResult implements IUserCardListResult {
  declare items: Array<UserCard>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * Follow
 */
class Follow extends ABaseModel implements IFollow {
  id: number;
  followerId: number;
  leaderId: number;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * FollowPaginationResult
 */
class FollowPaginationResult extends PaginationResult implements IFollowPaginationResult {
  declare items: Array<Follow>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * FollowListResult
 */
class FollowListResult extends ListResult implements IFollowListResult {
  declare items: Array<Follow>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

export {
  User,
  PrivacySetting,
  Account,
  Author,
  AuthorLocale,
  CuratorThought,
  CuratorThoughtPaginationResult,
  CustomerCredit,
  UserAddress,
  UserAddressListResult,
  UserCard,
  UserCardListResult,
  Follow,
  FollowPaginationResult,
  FollowListResult,
};
